import * as React from 'react';
import { Dialog } from '@ampm-7en-ai/ui/components/Dialog';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { useToast } from '@/hooks/use-toast';
import { useCreateTopupPackage, useUpdateTopupPackage, type TopupPackage } from '@/hooks/useTopupPackages';
import { CURRENCY_SYMBOL } from '@/lib/money';

/**
 * A bundle of replies at a fixed price.
 *
 * The two numbers decide what a customer is really paying, so the price per
 * reply is worked out as you type rather than left to be checked afterwards.
 */
export default function TopupPackageDialogV2({
  open,
  onOpenChange,
  editPackage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editPackage?: TopupPackage | null;
}) {
  const { toast } = useToast();
  const create = useCreateTopupPackage();
  const update = useUpdateTopupPackage();
  const saving = create.isPending || update.isPending;

  const [name, setName] = React.useState('');
  const [replies, setReplies] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [active, setActive] = React.useState(true);

  // Each opening starts from what is being edited, or from nothing.
  React.useEffect(() => {
    if (!open) return;
    setName(editPackage?.name ?? '');
    setReplies(editPackage ? String(editPackage.replies) : '');
    setAmount(editPackage?.amount ?? '');
    setActive((editPackage?.status ?? 'ACTIVE') === 'ACTIVE');
  }, [open, editPackage]);

  const replyCount = Number.parseInt(replies, 10);
  const total = Number.parseFloat(amount);
  const each = Number.isFinite(replyCount) && replyCount > 0 && Number.isFinite(total) ? total / replyCount : null;

  const blocked = !name.trim() || !(replyCount > 0) || !(total >= 0);

  const save = async () => {
    if (blocked) return;
    const payload = { name: name.trim(), replies: replyCount, amount: total, status: active ? 'ACTIVE' : 'INACTIVE' } as const;
    try {
      if (editPackage) await update.mutateAsync({ id: editPackage.id, ...payload });
      else await create.mutateAsync(payload);
      toast({ title: `${payload.name} ${editPackage ? 'updated' : 'created'}` });
      onOpenChange(false);
    } catch (failure) {
      toast({
        title: 'That didn’t save',
        description: failure instanceof Error ? failure.message : 'The package wasn’t saved.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editPackage ? 'Edit package' : 'New package'}
      description="A set number of replies, sold for one price."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || blocked}>
            {saving ? 'Saving…' : editPackage ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="package-name">
          <Input id="package-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Starter top-up" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Replies" htmlFor="package-replies">
            <Input
              id="package-replies"
              type="number"
              min={1}
              value={replies}
              onChange={(e) => setReplies(e.target.value)}
              placeholder="1000"
            />
          </Field>
          <Field label={`Price (${CURRENCY_SYMBOL})`} htmlFor="package-amount">
            <Input
              id="package-amount"
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="49.00"
            />
          </Field>
        </div>

        {each !== null && (
          <p className="text-ui-xs text-ui-fg-muted">
            That works out at {CURRENCY_SYMBOL}
            {each.toFixed(4)} a reply.
          </p>
        )}

        <ToggleRow
          label="Available to buy"
          description="Switched off, it stays set up but nobody can buy it."
          checked={active}
          onCheckedChange={setActive}
        />
      </div>
    </Dialog>
  );
}
